import type { Geometry } from 'geojson'
import BufferGeometryWorker from './bufferGeometry.worker.ts?worker'
import type { BufferReportGeometryResult } from './bufferReportGeometry.ts'

export type BufferGeometryRequest = { geometry: Geometry; radiusKm: number; maxVertexCount: number; bufferSteps: number }

type Job = { request: BufferGeometryRequest; resolve: (result: BufferReportGeometryResult | undefined) => void }

// A worker that runs out of memory means the geometry is too large to buffer
const WORKER_FAILED: BufferReportGeometryResult = { success: false, reason: 'geometry-too-complex' }

// One worker for every report form, so that what it remembers carries from one request to the next
let worker: Worker | undefined
let running: Job | undefined
let waiting: Job | undefined

// One job runs at a time. A job that has to wait is replaced by a newer one, because only the latest selection
// matters, and a replaced job resolves to `undefined`.
export const bufferGeometry = (request: BufferGeometryRequest) =>
	new Promise<BufferReportGeometryResult | undefined>((resolve) => {
		const job = { request, resolve }
		if (!running) return start(job)

		waiting?.resolve(undefined)
		waiting = job
	})

const start = (job: Job) => {
	running = job
	worker ??= startWorker()
	worker.postMessage(job.request)
}

// There is one job in flight, so the answer that comes back is always for it
const finish = (result: BufferReportGeometryResult) => {
	running?.resolve(result)
	running = undefined

	const next = waiting
	waiting = undefined
	if (next) start(next)
}

const startWorker = () => {
	const started = new BufferGeometryWorker()
	started.onmessage = ({ data }: MessageEvent<BufferReportGeometryResult>) => finish(data)
	started.onerror = () => {
		started.terminate()
		worker = undefined
		finish(WORKER_FAILED)
	}
	return started
}
